import type { Theme } from '@mui/material';
import {
  AssetDisplayType,
  BI_ASSET_TYPES,
  ModelArtifactType,
  TreeType,
} from '../types/types';

export const numberWithCommas = (
  x: number = 0,
  precision: number | undefined = undefined,
) => {
  let xString = x.toString();
  if (precision !== undefined) {
    xString = x.toFixed(precision);
  }
  if (Number(xString) % 1 === 0) {
    xString = x.toFixed(0);
  }
  return xString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatDuration = (seconds?: number): string => {
  if (seconds === undefined || seconds === null) {
    return 'N/A';
  }
  let unit = 'Secs';
  let val = seconds;
  if (seconds >= 3600) {
    val = seconds / 3600;
    unit = 'Hours';
  } else if (seconds >= 60) {
    val = seconds / 60;
    unit = 'Mins';
  }
  const fixedVal =
    Number(val.toFixed(1)) % 1 === 0 ? val.toFixed(0) : val.toFixed(1);
  return `${fixedVal} ${unit}`;
};

export const getValue = (obj: any, str: string) =>
  str.split('.').reduce((p: any, prop: string) => (p || {})[prop], obj);

export const groupBy = <T, P extends string | number | symbol>(
  arr: T[],
  cb: (item: T) => P,
): Record<P, T[]> =>
  arr.reduce((group: any, item) => {
    const groupName = cb(item);
    group[groupName] = group[groupName] ?? [];
    group[groupName].push(item);
    return group;
  }, {});

export const distinctArrayBy = <T>(arr: T[], key: string): T[] =>
  Array.from(
    new Map(arr.map((item: any) => [getValue(item, key), item])).values(),
  );

export const distinctArray = (arr: string[]) => Array.from(new Set(arr));

export const intersectArrays = (arr1: string[], arr2: string[]) =>
  arr1.filter((a) => arr2.some((b) => b === a));

export const intersectArraysBy = <T>(arr1: T[], arr2: T[], key: string): T[] =>
  arr1.filter((a: any) => arr2.some((b: any) => b[key] === a[key]));

export const omitArrayBy = <T>(arr1: T[], arr2: T[], key: string): T[] =>
  arr1.filter((a: any) => arr2.every((b: any) => b[key] !== a[key]));

export const compareArraysValues = (
  arr1: string[],
  arr2: string[],
): boolean => {
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = [...arr1].sort();
  const sortedArr2 = [...arr2].sort();

  for (let i = 0; i < sortedArr1.length; i += 1) {
    if (sortedArr1[i] !== sortedArr2[i]) return false;
  }
  return true;
};

export const getModelTitle = (model: Record<string, any>) =>
  `${model.database_name ? `${model.database_name}.` : ''}${
    model.schema_name
  }.${model.table_name}`.toLowerCase();

export const capitalize = (str: string | undefined = '') =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const capitalizeWords = (str: string | undefined = '') =>
  str
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');

export const sumArr = (arr: number[]) =>
  arr.reduce((partialSum, a) => partialSum + a, 0);

export const getFirstLettersOfName = (name: string) => {
  const nameFixed = name.replace('@', '').trim();
  const splitName = nameFixed.split(' ').filter((word) => !!word);

  if (splitName.length === 0) {
    return '';
  }

  if (splitName.length === 1) {
    return nameFixed[0].toUpperCase();
  }

  const [firstName, lastName] = splitName;
  return `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`;
};

export const getAvatarColors = (name: string, { palette }: Theme) => {
  const { avatars } = palette.identity;
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) {
    sum += name.charCodeAt(i);
  }
  return avatars[sum % avatars.length];
};

export const getShortTimeUnitString = (timeStr: string) =>
  timeStr
    .replace('seconds', 'sec')
    .replace('second', 'sec')
    .replace('minutes', 'min')
    .replace('minute', 'min');

export const formatNumbersToShowDiff = (num1: number, num2: number) => {
  const diff = Math.abs(num1 - num2);

  if (diff >= 1 || diff === 0) {
    return [Number(num1.toFixed(2)), Number(num2.toFixed(2))];
  }

  const remainder = diff.toString().split('.')[1];
  let zeroCount = 0;

  for (let i = 0; i < remainder.length; i += 1) {
    const char = remainder[i];
    if (char === '0') {
      zeroCount += 1;
    } else {
      break;
    }
  }

  return [
    Number(num1.toFixed(zeroCount + 1)),
    Number(num2.toFixed(zeroCount + 1)),
  ];
};

export const isElementsColliding = (
  aRef: any,
  bRef: any,
  margin: number = 0,
) => {
  if (!aRef.current || !bRef.current) {
    return false;
  }

  const aRect = aRef.current.getBoundingClientRect();
  const bRect = bRef.current.getBoundingClientRect();

  return (
    aRect.left - margin < bRect.right + margin &&
    aRect.right + margin > bRect.left - margin &&
    aRect.top - margin < bRect.bottom + margin &&
    aRect.bottom + margin > bRect.top - margin
  );
};
export const trimEmptyLines = (text: string): string =>
  // Use a regular expression to remove empty lines from the beginning and end of the string.
  text.replace(/^\s*[\r\n\s]+|\s*[\r\n\s]+$/g, '');

export const pluralizeString = (
  num: number,
  singularForm: string,
  pluralForm: string,
): string => {
  if (num === 1) {
    return `${num} ${singularForm}`;
  }
  return `${num} ${pluralForm}`;
};

export const isElementaryCloudTest = (testType: string = ''): boolean =>
  testType.startsWith('elementary_cloud');

export const isAutomatedMonitorsTest = (testSubType: string): boolean =>
  testSubType === 'automated';

export const isContentTest = (testSubType: string): boolean =>
  testSubType === 'content';

export const isCloudTest = (testSubType: string): boolean =>
  testSubType === 'cloud';

export const isNumeric = (value: string) => /^-?\d+$/.test(value);

export const isValidFileName = (value: string) => /^[a-zA-Z0-9_]+$/.test(value);

export const isInSaaSMode = () => !!window.axiosInstance;

export const statusLabelMap: Record<string, string> = {
  'partial success': 'Partial Success',
  fail: 'Failure',
  warn: 'Warning',
  warning: 'Warning',
  pass: 'Passed',
  success: 'Success',
  error: 'Error',
  skipped: 'Skipped',
  no_data: 'No data',
};

export const debugLog = (...args: any[]) => {
  // Change this const to enable debugging
  const enableDebug = false;

  if (enableDebug) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

export const isBiAsset = (displayType: AssetDisplayType): boolean => {
  const BI_ASSET_TYPES_SET = new Set<string>(BI_ASSET_TYPES);
  return BI_ASSET_TYPES_SET.has(displayType);
};

export const getTreeTypeByDisplayType = (
  displayType: AssetDisplayType,
): TreeType => (isBiAsset(displayType) ? 'bi_assets' : 'data_assets');

export const getTreeTypeByArtifactType = (
  artifactType: ModelArtifactType,
): TreeType => (artifactType === 'exposure' ? 'bi_assets' : 'data_assets');
